import { ApiResult, request, safeRequest } from '@synergy/api';
import { noop } from '@synergy/utils';

import { InternalSettings } from '../models/settings';
import { SystemNotification } from '../models/notifications';

export type ApplicationLogResult = {
    timestamp: string | null;
    message: string;
};

export type ApplicationLogLevel =
    | 'DEBUG'
    | 'INFO'
    | 'NOTICE'
    | 'WARNING'
    | 'ERROR'
    | 'CRITICAL'
    | 'ALERT'
    | 'EMERGENCY';

export type ApplicationLogSearchFilters = {
    query?: string | null;
    level?: ApplicationLogLevel | null;
    from?: string | null;
    to?: string | null;
    limit?: number;
    offset?: number;
};

export class SettingsApi {
    constructor(protected baseUrl: string) {}

    public clearCache(): Promise<ApiResult> {
        return safeRequest(() => request.post(`${this.baseUrl}/settings/clear-cache`), noop);
    }

    public saveInternalSettings(
        lobsterXmlOrderUrl: string | null,
        defaultCfpTextContract: string | null,
        defaultCfpTextBonus: string | null,
        statusScale: Record<string, string> | null
    ): Promise<ApiResult> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/settings/internal`, {
                    lobsterXmlOrderUrl,
                    defaultCfpTextContract,
                    defaultCfpTextBonus,
                    statusScale,
                }),
            noop
        );
    }

    public getInternalSettings(): Promise<ApiResult<InternalSettings>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/settings/internal`),
            response => response.data
        );
    }

    public postNotification(notification: SystemNotification): Promise<ApiResult<SystemNotification>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/system-notifications`, {
                    type: notification.type,
                    email: notification.email,
                }),
            response => response.data
        );
    }

    public patchNotification(notification: SystemNotification): Promise<ApiResult<SystemNotification>> {
        return safeRequest(
            () =>
                request.patch(`${this.baseUrl}/system-notifications/${notification.id}`, {
                    type: notification.type,
                    email: notification.email,
                }),
            response => response.data
        );
    }

    public deleteNotification(id: number): Promise<ApiResult<SystemNotification>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/system-notifications/${id}`),
            response => response.data
        );
    }

    public getNotifications(): Promise<ApiResult<SystemNotification[]>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/system-notifications`),
            response => response.data
        );
    }

    public searchApplicationLogs(
        handlerName: string,
        filters: ApplicationLogSearchFilters = {}
    ): Promise<ApiResult<ApplicationLogResult[]>> {
        const params = new URLSearchParams();
        if (filters.query) params.set('query', filters.query);
        if (filters.level) params.set('level', filters.level);
        if (filters.from) params.set('from', filters.from);
        if (filters.to) params.set('to', filters.to);
        if (filters.limit !== undefined) params.set('limit', String(filters.limit));
        if (filters.offset !== undefined) params.set('offset', String(filters.offset));

        const qs = params.toString();
        const url = `${this.baseUrl}/settings/application-logs/${encodeURIComponent(handlerName)}/search${
            qs.length > 0 ? `?${qs}` : ''
        }`;
        return safeRequest(
            () => request.get(url),
            response => response.data
        );
    }
}
